import { authFetch } from '@/auth/token-interceptor';
import { env } from '@/env';

const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

export type UploadImageKind = 'logo' | 'cover' | 'gallery' | 'product' | 'allergen-sheet';
export type ImageUploadErrorCode = 'tooLarge' | 'wrongType' | 'failed';

export class ImageUploadError extends Error {
  constructor(public readonly code: ImageUploadErrorCode) {
    super(code);
    this.name = 'ImageUploadError';
  }
}

function isHeicLike(file: File): boolean {
  const type = file.type.toLowerCase();
  if (type === 'image/heic' || type === 'image/heif') return true;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext === 'heic' || ext === 'heif';
}

async function normalizeFile(file: File): Promise<File> {
  if (!isHeicLike(file)) return file;
  const heic2any = (await import('heic2any')).default;
  const converted = await heic2any({ blob: file, toType: 'image/jpeg' });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  const jpegName = `${file.name.replace(/\.(heic|heif)$/i, '')}.jpg`;
  return new File([blob], jpegName, { type: 'image/jpeg' });
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

async function resizeImage(file: File, maxEdge: number): Promise<File> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const longest = Math.max(img.width, img.height);
    if (longest <= maxEdge) return file;
    const scale = maxEdge / longest;
    const width = Math.round(img.width * scale);
    const height = Math.round(img.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85),
    );
    if (!blob) return file;
    const name = `${file.name.replace(/\.[^.]+$/, '')}.jpg`;
    return new File([blob], name, { type: 'image/jpeg' });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function uploadImage(
  rawFile: File,
  restaurantId: string,
  kind: UploadImageKind,
  opts?: { maxEdge?: number },
): Promise<{ key: string }> {
  if (rawFile.size > MAX_SIZE_BYTES) throw new ImageUploadError('tooLarge');

  let file: File;
  try {
    file = await normalizeFile(rawFile);
    if (opts?.maxEdge) file = await resizeImage(file, opts.maxEdge);
  } catch {
    throw new ImageUploadError('failed');
  }

  if (!ALLOWED_MIME_TYPES.includes(file.type)) throw new ImageUploadError('wrongType');
  if (file.size > MAX_SIZE_BYTES) throw new ImageUploadError('tooLarge');

  const presignRes = await authFetch(`${env.apiUrl}/api/files/upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ restaurantId, kind, contentType: file.type, sizeBytes: file.size }),
  });
  if (!presignRes.ok) throw new ImageUploadError('failed');
  const presigned = (await presignRes.json().catch(() => null)) as {
    uploadUrl?: unknown;
    key?: unknown;
  } | null;
  const uploadUrl = presigned?.uploadUrl;
  const key = presigned?.key;
  if (typeof uploadUrl !== 'string' || typeof key !== 'string') {
    throw new ImageUploadError('failed');
  }

  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!putRes.ok) throw new ImageUploadError('failed');

  return { key };
}
